import React from 'react';
import { Form, Badge, Radio, Tooltip, Modal } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faShieldHalved, faPen } from '@fortawesome/free-solid-svg-icons';
import { CheckOutlined } from '@ant-design/icons';
import {
  SettingsCard,
  CardHeader,
  CardTitle,
  CardTitleText,
  PreferenceRow,
  PreferenceInfo,
  PreferenceLabel,
  PreferenceDescription,
  FormSection,
  SaveChangesButton,
  CancelButton,
  DisableButton,
  DisableButtonWrap,
  ActionButtons,
  StyledSwitch,
  ActionIconButton,
  StyledInput,
  TwoStepMethodOption,
  TwoStepInputWrap,
  TwoStepEnableBlock,
  TwoStepEnableTitle,
  TwoStepEnableDescription,
  TwoStepSwitchWrap,
  ResendCodeButton,
  ModalWarningText,
} from '../Styles';
import type { TwoStepVerificationSectionProps, TwoFactorMethod } from '../types';
import {
  maskValue,
  formatExpiresAt,
  CARD_ICON_STYLE,
  INFO_ICON_STYLE,
  TWO_FACTOR_METHODS,
  TWO_FACTOR_FIELD_CONFIG,
} from '../constants';

const TwoStepVerificationSection: React.FC<TwoStepVerificationSectionProps> = ({
  editingSection,
  formData,
  profileLoading,
  setupLoading,
  twoFactorCode,
  twoFactorSetupInProgress,
  expiresAt,
  onEdit,
  onSwitchChange,
  onInputChange,
  onMethodChange,
  onCancel,
  onSetupTwoFactor,
  onTwoFactorCodeChange,
  onConfirmTwoFactor,
  onResendTwoFactor,
  onDisableTwoFactor,
}) => {
  const [displayTime, setDisplayTime] = React.useState('');
  const [prevMethod, setPrevMethod] = React.useState(formData.twoFactorMethod);
  const [prevContact, setPrevContact] = React.useState(
    formData.twoFactorMethod === 'phone' ? formData.twoFactorPhone : formData.twoFactorEmail
  );
  const [showDisableConfirmModal, setShowDisableConfirmModal] = React.useState(false);

  const hasContactChanged = React.useMemo(() => {
    if (!twoFactorSetupInProgress) return false;
    const currentContact = formData.twoFactorMethod === 'phone'
      ? formData.twoFactorPhone
      : formData.twoFactorEmail;
    return prevMethod !== formData.twoFactorMethod || prevContact !== currentContact;
  }, [twoFactorSetupInProgress, formData.twoFactorMethod, formData.twoFactorPhone, formData.twoFactorEmail, prevMethod, prevContact]);

  React.useEffect(() => {
    if (!expiresAt || !twoFactorSetupInProgress) { setDisplayTime(''); return; }
    const updateTimer = () => setDisplayTime(formatExpiresAt(expiresAt));
    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, [expiresAt, twoFactorSetupInProgress]);

  React.useEffect(() => {
    if (!twoFactorSetupInProgress) {
      setPrevMethod(formData.twoFactorMethod);
      setPrevContact(formData.twoFactorMethod === 'phone' ? formData.twoFactorPhone : formData.twoFactorEmail);
    }
  }, [twoFactorSetupInProgress, formData.twoFactorMethod, formData.twoFactorPhone, formData.twoFactorEmail]);

  const isEditing = editingSection === 'twoStep';
  const isDisabled = profileLoading || formData.twoFactorEnabled;
  const displayMethod = formData.twoFactorMethod === 'phone' ? 'Phone' : 'Email';
  const displayValue = formData.twoFactorMethod === 'phone'
    ? maskValue(formData.twoFactorPhone)
    : maskValue(formData.twoFactorEmail);

  const primaryAction = React.useMemo(() => {
    if (twoFactorSetupInProgress && hasContactChanged)
      return { label: 'Send Verification Code', handler: onSetupTwoFactor, disabled: false };
    if (twoFactorSetupInProgress && onConfirmTwoFactor)
      return { label: 'Verify & Enable 2FA', handler: onConfirmTwoFactor, disabled: !formData.twoFactorEnabled };
    if (onSetupTwoFactor)
      return { label: 'Send Verification Code', handler: onSetupTwoFactor, disabled: false };
    return null;
  }, [twoFactorSetupInProgress, hasContactChanged, onConfirmTwoFactor, onSetupTwoFactor, formData.twoFactorEnabled]);

  const fieldCfg = TWO_FACTOR_FIELD_CONFIG[formData.twoFactorMethod];

  const handleDisableClick = () => setShowDisableConfirmModal(true);
  const handleDisableConfirm = () => { setShowDisableConfirmModal(false); onDisableTwoFactor?.(); };

  return (
    <SettingsCard>
      <CardHeader>
        <CardTitle>
          <FontAwesomeIcon icon={faShieldHalved} style={CARD_ICON_STYLE} />
          <CardTitleText level={4}>Two-Step Verification</CardTitleText>
        </CardTitle>
        <Tooltip
          title={formData.twoFactorEnabled ? 'Disable 2FA to edit settings' : profileLoading ? 'Loading...' : ''}
          placement="top"
        >
          <ActionIconButton onClick={isDisabled ? undefined : onEdit} iconColor="#2D6CCA" $loading={isDisabled}>
            <FontAwesomeIcon icon={faPen} fontSize={14} />
          </ActionIconButton>
        </Tooltip>
      </CardHeader>

      {!isEditing ? (
        <>
          <PreferenceRow>
            <FontAwesomeIcon icon={faShieldHalved} style={INFO_ICON_STYLE} />
            <PreferenceInfo>
              <PreferenceLabel>Two-Step Verification</PreferenceLabel>
              <PreferenceDescription>
                Add an extra layer of security using your personal phone number or email
              </PreferenceDescription>
            </PreferenceInfo>
            <Badge
              status={formData.twoFactorEnabled ? 'success' : 'default'}
              text={formData.twoFactorEnabled ? 'Enabled' : 'Disabled'}
            />
          </PreferenceRow>
          {formData.twoFactorEnabled && (
            <>
              <PreferenceRow>
                <PreferenceInfo>
                  <PreferenceLabel>Verification method</PreferenceLabel>
                  <PreferenceDescription>{displayMethod}: {displayValue}</PreferenceDescription>
                </PreferenceInfo>
              </PreferenceRow>
              {onDisableTwoFactor && (
                <DisableButtonWrap>
                  <DisableButton onClick={handleDisableClick} loading={setupLoading}>
                    Disable Two-Step Verification
                  </DisableButton>
                </DisableButtonWrap>
              )}
            </>
          )}
        </>
      ) : (
        <FormSection>
          <Form layout="vertical">
            <Form.Item label="Enable Two-Step Verification">
              <TwoStepEnableBlock>
                <PreferenceInfo>
                  <TwoStepEnableTitle>Two-Step Verification</TwoStepEnableTitle>
                  <TwoStepEnableDescription>
                    Verify your identity using a code sent to your personal phone or email
                  </TwoStepEnableDescription>
                </PreferenceInfo>
                <TwoStepSwitchWrap>
                  <StyledSwitch
                    checked={formData.twoFactorEnabled}
                    onChange={(checked) => onSwitchChange('twoFactorEnabled', checked)}
                  />
                </TwoStepSwitchWrap>
              </TwoStepEnableBlock>
            </Form.Item>

            {formData.twoFactorEnabled && (
              <>
                <Form.Item label="Verification method">
                  <Radio.Group
                    value={formData.twoFactorMethod}
                    onChange={(e) => onMethodChange(e.target.value as TwoFactorMethod)}
                  >
                    {TWO_FACTOR_METHODS.map(({ value, icon, label }) => (
                      <TwoStepMethodOption key={value} value={value}>
                        <FontAwesomeIcon icon={icon} />
                        <span>{label}</span>
                      </TwoStepMethodOption>
                    ))}
                  </Radio.Group>
                </Form.Item>

                <Form.Item label={fieldCfg.label}>
                  <TwoStepInputWrap>
                    <StyledInput
                      name={fieldCfg.name}
                      value={formData[fieldCfg.name]}
                      onChange={onInputChange}
                      placeholder={fieldCfg.placeholder}
                      type={fieldCfg.type}
                      maxLength={fieldCfg.maxLength}
                    />
                  </TwoStepInputWrap>
                </Form.Item>

                {twoFactorSetupInProgress && (
                  <Form.Item label="Verification Code" extra={displayTime}>
                    <TwoStepInputWrap>
                      <StyledInput
                        name="twoFactorCode"
                        value={twoFactorCode || ''}
                        onChange={(e) => onTwoFactorCodeChange?.(e.target.value)}
                        placeholder="Enter 6-digit code"
                        type="text"
                        maxLength={6}
                      />
                    </TwoStepInputWrap>
                    {onResendTwoFactor && (
                      <ResendCodeButton type="button" onClick={onResendTwoFactor}>
                        Resend Code
                      </ResendCodeButton>
                    )}
                  </Form.Item>
                )}
              </>
            )}
          </Form>

          {formData.twoFactorEnabled && primaryAction && (
            <ActionButtons>
              <SaveChangesButton
                icon={<CheckOutlined />}
                onClick={primaryAction.handler}
                loading={setupLoading}
                disabled={primaryAction.disabled}
              >
                {primaryAction.label}
              </SaveChangesButton>
              <CancelButton onClick={onCancel}>Cancel</CancelButton>
            </ActionButtons>
          )}
        </FormSection>
      )}

      <Modal
        title="Disable Two-Step Verification?"
        open={showDisableConfirmModal}
        onOk={handleDisableConfirm}
        onCancel={() => setShowDisableConfirmModal(false)}
        okText="Disable"
        cancelText="Cancel"
        okButtonProps={{ danger: true }}
        centered
      >
        <p>Are you sure you want to disable Two-Step Verification? This will reduce the security of your account.</p>
        <ModalWarningText>
          You will no longer need to enter a verification code when logging in.
        </ModalWarningText>
      </Modal>
    </SettingsCard>
  );
};

export default React.memo(TwoStepVerificationSection);
